import React, { useMemo } from 'react';
import { InView } from 'react-intersection-observer';

import { SecondaryButton } from '@components/Button/variants';
import AuthorUnit from '@components/FeedItems/AuthorUnit';
import useEventTracking from '@hooks/useEventTracking';
import type { ForumPost } from '@interfaces/homepage';
import { forum_post_path } from '@routes';
import { ClickTarget, TrackingLocation, click, impression } from '@tracking';

import MyTISection from './Section';
import { MY_TI_ENGAGEMENT_LABELS, MY_TI_SECTION_IDS, VIEW_ALL } from './constants';
import type { MyTICommunityInsightsProps } from './interfaces';
import useRegisterSection from './useRegisterSection';

const MyTICommunityInsights = ({ forumPosts, initialCurrentUser }: MyTICommunityInsightsProps) => {
  const { Track, trackEvent } = useEventTracking({
    location: TrackingLocation.ForumUnit,
  });

  const posts = forumPosts?.content as ForumPost[] | undefined;

  const communityInsightsDisplayText = useMemo(() => {
    if (!initialCurrentUser) return MY_TI_ENGAGEMENT_LABELS.NotEngaged.CommunityInsights;
    const userHasCommented = initialCurrentUser?.has_commented_in_forum;

    return userHasCommented
      ? MY_TI_ENGAGEMENT_LABELS.Engaged.CommunityInsights
      : MY_TI_ENGAGEMENT_LABELS.NotEngaged.CommunityInsights;
  }, [initialCurrentUser]);

  useRegisterSection(MY_TI_SECTION_IDS.CommunityInsights, !!(posts && posts.length > 0));

  if (!posts || posts.length === 0) return null;

  const onForumPostClick = (post: ForumPost) => {
    trackEvent(
      click({ clickTarget: ClickTarget.ForumPost, contentId: post.id, clickText: post.title })
    );
  };

  const onViewAllClick = () => {
    trackEvent(click({ clickTarget: ClickTarget.ViewMore, clickText: VIEW_ALL }));
  };

  return (
    <Track>
      <MyTISection
        id={MY_TI_SECTION_IDS.CommunityInsights}
        highlight={communityInsightsDisplayText.label}
        subtitle={communityInsightsDisplayText.subtitle}
        title={communityInsightsDisplayText.title}
      >
        <InView
          onChange={(inView) => {
            inView &&
              trackEvent(impression({ impressionElement: MY_TI_SECTION_IDS.CommunityInsights }));
          }}
          triggerOnce
        >
          <div className="relative mt-32 md:mt-48">
            <div className="grid grid-cols-2 gap-32 md:grid-cols-4 md:gap-24">
              {posts.map((post) => (
                <article
                  key={post.id}
                  className="flex flex-col justify-between gap-24 border-0 border-r border-solid border-grayscale-gray pr-24 last-of-type:border-r-0 last-of-type:pr-0"
                >
                  <a
                    className="mt-16 block font-suisse-neue text-body-md font-normal text-black no-underline transition-colors duration-150 hover:text-primary-magenta"
                    href={forum_post_path(post.id)}
                    onClickCapture={() => onForumPostClick(post)}
                  >
                    {post.title}
                  </a>
                  <AuthorUnit author={{ ...post.author, profileEnabled: true }} isRightRail />
                </article>
              ))}
            </div>
            <SecondaryButton
              as="link"
              className="mt-32 md:mt-48"
              href={forumPosts.viewAllUrl}
              onClickCapture={onViewAllClick}
            >
              {VIEW_ALL}
            </SecondaryButton>
          </div>
        </InView>
      </MyTISection>
    </Track>
  );
};

export default MyTICommunityInsights;
